import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { AnchoredTooltipProps } from './types';
import { styles } from './styles';

let tooltipIdCounter = 0;

export const AnchoredTooltip: React.FC<AnchoredTooltipProps> = ({
  content,
  isOpen,
  onClose,
  children,
  className,
  header,
  width = 288,
}) => {
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerId = useMemo(() => `tooltip-${++tooltipIdCounter}`, []);

  const calculatePosition = useCallback(
    (rect: DOMRect) => {
      const viewportWidth = window.innerWidth;

      // Check if tooltip would overflow on the right
      let left = rect.left;
      if (rect.left + width > viewportWidth) {
        // Align tooltip's right edge with tag's right edge
        left = rect.right - width;
        // If still overflowing, align to viewport edge with padding
        if (left < 8) {
          left = 8;
        }
      }

      return {
        top: rect.bottom + 4,
        left,
      };
    },
    [width]
  );

  const updatePosition = useCallback(() => {
    const triggerElement = document.querySelector(`[data-tooltip-trigger="${triggerId}"]`);
    if (triggerElement) {
      const rect = triggerElement.getBoundingClientRect();
      setPosition(calculatePosition(rect));
    }
  }, [triggerId, calculatePosition]);

  // Update position when tooltip opens
  useEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen, updatePosition]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isClickOnTrigger = target.closest(`[data-tooltip-trigger="${triggerId}"]`);
      const isClickOnTooltip = target.closest(`[data-tooltip-id="${triggerId}"]`);

      if (!isClickOnTrigger && !isClickOnTooltip) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, triggerId, onClose]);

  // Update position on scroll and resize
  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  return (
    <>
      <span data-tooltip-trigger={triggerId} className={styles.wrapper}>
        {children}
      </span>

      {isOpen &&
        createPortal(
          <div
            data-tooltip-id={triggerId}
            className={clsx(styles.tooltip, className)}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width,
            }}
          >
            {header && <div className={styles.header}>{header}</div>}
            <div className={styles.content}>{content}</div>
          </div>,
          document.body
        )}
    </>
  );
};

export type { AnchoredTooltipProps } from './types';
